import React, { FC, ChangeEventHandler, ComponentProps } from 'react';
import styled, { css } from 'styled-components';

import { Select } from 'src/components/Select';
import { HorizontalField } from 'src/components/Field';

import { generatePages, SEPARATOR } from './generatePages';

type ListPageSizes = 20 | 50 | 100 | 250;

const LIST_PAGE_SIZES: ListPageSizes[] = [20, 50, 100, 250];

interface PaginationProps {
  testId?: string;
  /**
   * Controlled property. A number of page a user is on.
   */
  currentPageNumber: number | string;
  /**
   * Controlled property. A number of items we display on one page.
   */
  currentPageSize: number | string;
  /**
   * Overall number of items backend stores
   */
  totalItems: number;
  /**
   * Executes on page size or page number change
   */
  onChange: (pageNumber: number, pageSize: number) => void;
  /**
   * Controls pagination styles when data is loading.
   * If true, disables buttons and grayscales the pagination.
   */
  isLoading?: boolean;
  /**
   * Page size selector field text. Default is 'Row: '
   */
  selectPageSizeText?: ComponentProps<typeof HorizontalField>['label'];
}

export const PaginationDeprecated: FC<PaginationProps> = ({
  currentPageNumber: externalPageNumber = 1,
  currentPageSize: externalPageSize = 20,
  totalItems,
  onChange,
  isLoading = false,
  selectPageSizeText = 'Rows:',
  testId,
}) => {
  const currentPageNumber = Number(externalPageNumber);
  const currentPageSize = Number(externalPageSize);

  const paginationTestId = `${testId ? `${testId}-pagination` : 'pagination'}`;

  const totalPages = Math.ceil(totalItems / currentPageSize);
  const pages = generatePages(currentPageNumber, totalPages);

  const handlePageClick = (newPage: number) => {
    onChange(newPage, currentPageSize);
  };

  const handlePageChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
    onChange(1, Number.parseInt(event.target.value, 10));
  };

  const handlePageOrSeparatorClick = (
    pageNumber: number | typeof SEPARATOR,
    index: number,
    pagesArray: (number | typeof SEPARATOR)[],
  ) => {
    if (pageNumber === SEPARATOR) {
      const isLeftSeparator = pagesArray.indexOf(SEPARATOR) === index;
      const pageModifier = isLeftSeparator ? -1 : 1;
      const nextPage = currentPageNumber + pageModifier;

      handlePageClick(nextPage);
    } else {
      handlePageClick(pageNumber);
    }
  };

  return (
    <PaginationWrapper data-test-id={paginationTestId} isLoading={isLoading}>
      <Pages>
        <Page
          isActive={currentPageNumber === 1}
          disabled={currentPageNumber <= 1}
          type="button"
          onClick={() => handlePageClick(currentPageNumber - 1)}
        >
          Previous
        </Page>
        {pages.map((pageNumber, i, array) => (
          <Page
            key={`${pageNumber}-${i}`}
            type="button"
            isActive={currentPageNumber === pageNumber}
            onClick={() => handlePageOrSeparatorClick(pageNumber, i, array)}
          >
            {pageNumber}
          </Page>
        ))}
        <Page
          type="button"
          isActive={currentPageNumber === totalPages}
          disabled={currentPageNumber >= totalPages}
          onClick={() => handlePageClick(currentPageNumber + 1)}
        >
          Next
        </Page>
      </Pages>
      <HorizontalField htmlFor="page-size" label={selectPageSizeText}>
        <Select
          id="page-size"
          options={LIST_PAGE_SIZES}
          value={currentPageSize}
          onChange={handlePageChange}
        />
      </HorizontalField>
    </PaginationWrapper>
  );
};

const PaginationWrapper = styled.span<{ isLoading?: boolean }>`
  display: flex;
  justify-content: space-between;

  ${({ isLoading }) =>
    isLoading &&
    css`
      pointer-events: none;
      opacity: 0.5;
      user-select: none;
    `}
`;

const Pages = styled.span`
  display: inline-block;
  border: 1px solid #bebebe; /* TODO: theme */
  border-radius: 2px;
`;

const Page = styled.button<{ isActive?: boolean; disabled?: boolean }>`
  background-color: #fff; /* TODO: theme */
  border: none;
  padding: 0.5em 1em; /* TODO: theme */
  font: inherit;
  cursor: pointer;

  ${({ isActive, disabled }) =>
    disabled
      ? css`
          cursor: not-allowed;
        `
      : css`
          ${isActive &&
            css`
              background-color: #f5f5f8; /* TODO: theme */
            `}
          &:hover {
            background-color: #f5f5f8; /* TODO: theme */
          }
        `}

  &:not(:last-child) {
    border-right: 1px solid #bebebe; /* TODO: theme */
  }

  @media (max-width: 1024px) {
    padding: 10px; /* TODO: theme */
  }
`;
